import clsx from 'clsx'
import { Button, Dropdown, DropdownItem, Grid, Icon, Popover, Typography } from 'gu-ui-kit'
import PropTypes from 'prop-types'
import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import useWriteToClipboard from 'utils/hooks/useWriteToClipboard'
import styles from 'components/Header/subcomponents/UserMenu/user-menu.module.scss'
import UserSwitcher from 'components/Header/subcomponents/UserSwitcher'
import useSwitchedUser from 'utils/hooks/useSwitchedUser'
import { CURRENCIES } from 'constants/app-config'
import { notificationShow } from 'modules/Notifications/actions'
import { LOGOUT } from 'modules/Auth/types'
import { ROUTES } from 'constants/routes'
import useGeneratePathI18n from 'utils/hooks/useGeneratePathI18n'
import { selectAppUser } from 'modules/App/store/selectors'

const UserMenu = ({ className }) => {
  const { bindTrigger, bindPopover, usePopupState } = Popover.usePopover()
  const writeToClipboard = useWriteToClipboard()

  const pathGen = useGeneratePathI18n()

  const { id, email, is_switch_user_available, is_agency_account_available } = useSelector(selectAppUser)

  const dispatch = useDispatch()
  const { t } = useTranslation(['common'])
  const { switchedUser, dropSwitchedUser } = useSwitchedUser()

  const popoverState = usePopupState({
    variant: 'popover',
    id: 'userMenuDropdown',
  })

  const toClipboard = useCallback((value) => {
    writeToClipboard(value)

    dispatch(
      notificationShow({
        type: 'success',
        message: 'common::notifications.success_copy_notification',
      }),
    )
  }, [])

  let timer = null

  const onMouseEnter = () => clearTimeout(timer)

  const onMouseLeave = () => {
    timer = setTimeout(() => {
      popoverState.close()
      clearTimeout(timer)
    }, 10000)
  }

  return (
    <>
      <Button
        {...bindTrigger(popoverState)}
        className={clsx(styles['user-menu__avatar'], className, {
          [styles['user-menu__avatar_switched']]: switchedUser,
          [styles['user-menu__avatar_switch-available']]: is_switch_user_available,
        })}
        variant={'ghost'}
        grey={!switchedUser}
        size={'s'}
        startIcon={<Icon name={'user'} />}
        noLabel
      />

      <Popover
        className={styles['popover']}
        {...bindPopover(popoverState)}
        bodyClassName={clsx({
          [styles['popover-body_switched']]: switchedUser,
        })}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 24,
          horizontal: 'left',
        }}
        breakpoints={{
          0: {
            variant: 'bottomSheet',
            hasBackdrop: true,
          },
          768: {
            variant: 'popover',
            hasBackdrop: false,
          },
        }}
      >
        <Dropdown
          className={styles['dropdown']}
          onMouseLeave={onMouseLeave}
          onMouseEnter={onMouseEnter}
        >
          {/*//user profile*/}
          <Grid
            className={styles['user-menu__user-profile']}
            direction={'column'}
            gap={16}
          >
            {is_switch_user_available && !is_agency_account_available && (
              <UserSwitcher
                email={email}
                id={id}
                className={clsx(
                  styles['user-menu__action-button'],
                  styles['user-menu__user-switcher'],
                )}
              />
            )}

            {switchedUser && (
              <div className={styles['user-menu__logged-with-previous-admin']}>
                {t('common::user_menu.logged_with_user')}
              </div>
            )}
            <Grid
              direction={'row'}
              wrap={false}
              gap={4}
            >
              <Grid
                fluid={false}
                className={clsx(
                  styles['user-menu__avatar'],
                  styles['user-menu__user-profile-avatar'],
                )}
              >
                <Icon
                  className="icon"
                  name={'user'}
                  color={switchedUser ? 'Accent-Notice-Primary' : 'Interface-GU-Primary'}
                  width={20}
                />
              </Grid>

              <Grid
                direction={'column'}
                alignItems={'flex-start'}
                className={styles['user-menu__user-profile-info']}
              >
                <Grid
                  gap={4}
                  direction={'column'}
                >
                  <Typography
                    color={'Label-Fifth'}
                    variant={'Body-15'}
                    component={'div'}
                    className={styles['user-menu__user-profile-email']}
                    title={email}
                  >
                    {email}
                  </Typography>

                  <Typography
                    variant={'Body-15'}
                    component={'span'}
                    color={'Label-Secondary'}
                    onClick={() => toClipboard(id)}
                    className={styles['user-menu__user-profile-id-copy']}
                  >
                    <span className={styles['user-menu__user-profile-id']}>ID: {id}</span>
                    <Icon
                      name={'copy-link'}
                      color={'inherit'}
                      width={20}
                    />
                  </Typography>
                </Grid>
              </Grid>

              {switchedUser && (
                <Grid
                  direction={'column'}
                  justifyContent={'center'}
                  fluid={false}
                >
                  <Button
                    color={'Interface-Eight'}
                    className={styles['user-menu__action-button']}
                    title={t('common::user_menu.logout')}
                    size={'s'}
                    variant={'ghost'}
                    onClick={dropSwitchedUser}
                    noLabel
                    startIcon={<Icon name={'login'} />}
                  />
                </Grid>
              )}
            </Grid>
            {/*{!switchedUser && (*/}
            <DropdownItem
              className={styles['user-menu__logout-button']}
              noHover
              size={'m'}
              component={'a'}
              href={pathGen(ROUTES.AUTH_LOGOUT)}
              onClick={(e) => {
                e.preventDefault()
                popoverState.close()
                dispatch({ type: LOGOUT })
              }}
              startIcon={
                <Icon
                  width={20}
                  name={'login'}
                  color={'Interface-Eight'}
                />
              }
            >
              {t('common::user_menu.logout')}
            </DropdownItem>
            {/*)}*/}
          </Grid>
        </Dropdown>
      </Popover>
    </>
  )
}

UserMenu.propTypes = {
  className: PropTypes.string,
  user: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  balance: PropTypes.shape({
    currency: PropTypes.oneOf(CURRENCIES),
  }),
}

export default UserMenu
